/**
 * Goods Receipt Drawer Component
 *
 * Drawer for displaying goods receipt details.
 * @see US-9.4 - Page Liste Receptions
 */

import { Check, X, Package, Building2, FileText, AlertTriangle } from 'lucide-react';
import { Drawer, Button, Badge, Skeleton } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes, formatQuantity } from '@/lib/format';
import { useGoodsReceipt, useValidateGoodsReceipt, useCancelGoodsReceipt } from '../hooks/useGoodsReceipts';
import { DocumentSection } from '@/features/documents';
import { useEntityDocuments, useUploadDocument } from '@/features/documents/hooks/useDocuments';
import { FileUpload } from '@/components/ui';
import { DocumentType } from '@bstock/shared';
import type { GoodsReceiptItemWithProduct } from '@bstock/shared';
import {
  GoodsReceiptStatus,
  GOODS_RECEIPT_STATUS_LABELS,
  GOODS_RECEIPT_STATUS_COLORS,
  VARIANCE_REASON_LABELS,
  VarianceReason,
} from '@bstock/shared';

interface GoodsReceiptDrawerProps {
  receiptId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showFinancials?: boolean;
  canValidate?: boolean;
  canCancel?: boolean;
}

/**
 * Map status to badge variant
 */
function getStatusVariant(status: GoodsReceiptStatus): 'success' | 'warning' | 'danger' | 'neutral' {
  const colorMap: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
    green: 'success',
    gray: 'neutral',
    red: 'danger',
  };
  return colorMap[GOODS_RECEIPT_STATUS_COLORS[status]] ?? 'neutral';
}

export function GoodsReceiptDrawer({ receiptId, open, onOpenChange, showFinancials = false, canValidate = false, canCancel = false }: GoodsReceiptDrawerProps) {
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const { data: receipt, isLoading } = useGoodsReceipt(receiptId);
  const validateMutation = useValidateGoodsReceipt();
  const cancelMutation = useCancelGoodsReceipt();
  const uploadMutation = useUploadDocument('goods-receipts', receiptId ?? undefined);
  const { data: documents } = useEntityDocuments('goods-receipts', receiptId ?? undefined);
  const hasBl = documents?.some((d) => d.documentType === DocumentType.GOODS_RECEIPT_BL_SUPPLIER);
  const hasInvoice = documents?.some((d) => d.documentType === DocumentType.GOODS_RECEIPT_INVOICE_SUPPLIER);

  const handleValidate = async () => {
    if (receipt && window.confirm(`Voulez-vous valider le BR ${receipt.receiptNumber} ?`)) {
      await validateMutation.mutateAsync(receipt.id);
    }
  };

  const handleCancel = async () => {
    if (receipt && window.confirm(`Voulez-vous annuler le BR ${receipt.receiptNumber} ?`)) {
      await cancelMutation.mutateAsync(receipt.id);
    }
  };

  const footer = receipt ? (
    <div className="flex gap-3">
      {canValidate && receipt.status === GoodsReceiptStatus.DRAFT && (
        <Button
          variant="primary"
          onClick={handleValidate}
          disabled={validateMutation.isPending}
        >
          <Check className="w-4 h-4" />
          Valider
        </Button>
      )}
      {canCancel && receipt.status === GoodsReceiptStatus.VALIDATED && (
        <Button
          variant="ghost"
          onClick={handleCancel}
          disabled={cancelMutation.isPending}
        >
          <X className="w-4 h-4" />
          Annuler
        </Button>
      )}
      <Button variant="ghost" onClick={() => onOpenChange(false)}>
        Fermer
      </Button>
    </div>
  ) : null;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={receipt ? `BR ${receipt.receiptNumber}` : 'Bon de reception'}
      description={receipt ? `Du ${formatDateShort(receipt.receiptDate)}` : undefined}
      size="wide"
      footer={footer}
    >
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-48" />
          <Skeleton className="h-32" />
        </div>
      ) : receipt ? (
        <div className="space-y-6">
          {/* Status Badge */}
          <div className="flex items-center gap-3">
            <Badge variant={getStatusVariant(receipt.status)}>
              {GOODS_RECEIPT_STATUS_LABELS[receipt.status]}
            </Badge>
            {!receipt.isDefinitive && (
              <Badge variant="warning">Provisoire</Badge>
            )}
            {receipt.purchaseOrder && (
              <span className="text-sm text-neutral-500">
                Commande: {receipt.purchaseOrder.orderNumber}
              </span>
            )}
          </div>

          {/* Info Section */}
          <div className="bg-neutral-50 rounded-lg p-4">
            <h3 className="font-medium text-dark-500 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Informations
            </h3>
            <dl className="space-y-3">
              <div className="flex justify-between text-sm">
                <dt className="text-neutral-500">Date de reception</dt>
                <dd className="text-dark-500">{formatDateShort(receipt.receiptDate)}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-neutral-500">Fournisseur</dt>
                <dd className="text-dark-500 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-neutral-400" />
                  {receipt.supplier.name}
                </dd>
              </div>
              {receipt.blNumber && (
                <div className="flex justify-between text-sm">
                  <dt className="text-neutral-500">N° Bon de livraison</dt>
                  <dd className="text-dark-500">{receipt.blNumber}</dd>
                </div>
              )}
              {receipt.supplierInvoiceRef && (
                <div className="flex justify-between text-sm">
                  <dt className="text-neutral-500">Ref. facture fournisseur</dt>
                  <dd className="text-dark-500">{receipt.supplierInvoiceRef}</dd>
                </div>
              )}
              {receipt.purchaseOrder && (
                <div className="flex justify-between text-sm">
                  <dt className="text-neutral-500">Commande liee</dt>
                  <dd className="text-dark-500">{receipt.purchaseOrder.orderNumber}</dd>
                </div>
              )}
              {showFinancials && (
                <div className="flex justify-between text-sm">
                  <dt className="text-neutral-500">Total HT</dt>
                  <dd className="font-semibold text-primary-600">
                    {formatCentimes(receipt.totalExclTax, currency)}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Items Section */}
          <div>
            <h3 className="font-medium text-dark-500 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Lignes ({receipt.items.length})
            </h3>
            <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-200">
              {receipt.items.map((item) => (
                <ReceiptItemRow key={item.id} item={item} currency={currency} showFinancials={showFinancials} />
              ))}
            </div>
          </div>

          {/* Notes */}
          {receipt.notes && (
            <div>
              <h3 className="font-medium text-dark-500 mb-2">Notes</h3>
              <p className="text-sm text-neutral-600 bg-neutral-50 rounded-lg p-3">
                {receipt.notes}
              </p>
            </div>
          )}

          {/* Upload documents manquants */}
          {(!hasBl || !hasInvoice) && (
            <div className="border-t border-neutral-200 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!hasBl && (
                  <FileUpload
                    label="BL fournisseur"
                    accept=".pdf,.jpg,.jpeg,.png"
                    maxSizeMB={10}
                    onFileSelect={(file) =>
                      uploadMutation.mutate({ file, documentType: DocumentType.GOODS_RECEIPT_BL_SUPPLIER })
                    }
                    uploading={uploadMutation.isPending}
                  />
                )}
                {!hasInvoice && (
                  <FileUpload
                    label="Facture fournisseur"
                    accept=".pdf,.jpg,.jpeg,.png"
                    maxSizeMB={10}
                    onFileSelect={(file) =>
                      uploadMutation.mutate({ file, documentType: DocumentType.GOODS_RECEIPT_INVOICE_SUPPLIER })
                    }
                    uploading={uploadMutation.isPending}
                  />
                )}
              </div>
            </div>
          )}

          {/* Documents */}
          <DocumentSection
            entityRoute="goods-receipts"
            entityId={receipt.id}
          />

          {/* Metadata */}
          <div className="text-xs text-neutral-400 space-y-1 border-t border-neutral-200 pt-4">
            <p>Cree le {formatDateShort(receipt.createdAt)}</p>
            {receipt.validatedAt && (
              <p>Valide le {formatDateShort(receipt.validatedAt)}</p>
            )}
            {receipt.cancelledAt && (
              <p>Annule le {formatDateShort(receipt.cancelledAt)}</p>
            )}
          </div>
        </div>
      ) : (
        <p className="text-neutral-500">Bon de reception non trouve</p>
      )}
    </Drawer>
  );
}

/**
 * Receipt Item Row
 */
function ReceiptItemRow({
  item,
  currency,
  showFinancials,
}: {
  item: GoodsReceiptItemWithProduct;
  currency: string;
  showFinancials: boolean;
}) {
  const hasVariance = item.varianceQuantity !== null && item.varianceQuantity !== 0;

  return (
    <div className="p-4 hover:bg-neutral-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-dark-500">{item.rawProductName}</span>
            <span className="text-xs text-neutral-400">{item.rawProduct.sku}</span>
          </div>
          <div className="text-sm text-neutral-500 mt-1">
            {item.packagingName}{showFinancials ? ` - ${formatCentimes(item.unitPriceExclTax, currency)}/u` : ''}
          </div>
        </div>

        <div className="text-right">
          <div className="font-medium text-dark-500">
            {item.quantityReceived}{showFinancials ? ` x ${formatCentimes(item.unitPriceExclTax, currency)}` : ` unité(s)`}
          </div>
          {showFinancials && (
            <div className="text-sm font-semibold text-primary-600">
              {formatCentimes(item.totalPriceExclTax, currency)}
            </div>
          )}
        </div>
      </div>

      {/* Variance indicator */}
      {hasVariance && (
        <div className="mt-2 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-warning-500" />
          <span className="text-warning-600">
            Ecart: {item.varianceQuantity! > 0 ? '+' : ''}{item.varianceQuantity}
            {item.varianceReason && (
              <> ({VARIANCE_REASON_LABELS[item.varianceReason as VarianceReason]})</>
            )}
          </span>
        </div>
      )}

      {/* Conversion info */}
      <div className="mt-1 text-xs text-neutral-400">
        = {formatQuantity(item.quantityInRecipeUnit)} {item.rawProduct.recipeUnit.symbol}
      </div>
    </div>
  );
}
